import arritechLogoSvg from './arritech-logo.svg?raw';
import qgenLogoSvg from './qgen-logo.svg?raw';
import qgenLogoMarkSvg from './qgen-logo-mark.svg?raw';

export interface BrandLogoSvg {
	/** Accessible label for this logo asset. */
	alt: string;
	/** SVG viewBox used when rendering the central logo content. */
	viewBox: string;
	/** Inner SVG markup shared by UI components and generated PDF payloads. */
	content: string;
	/** Complete SVG markup for consumers that need a standalone image, such as PDFs. */
	svg: string;
}

const viewBoxPattern = /viewBox="([^"]+)"/;
const svgContentPattern = /<svg\b[^>]*>([\s\S]*?)<\/svg>/;

/**
 * Converts a central SVG file into the normalized logo data used by UI and PDFs.
 */
export const createBrandLogoSvg = (alt: string, svg: string): BrandLogoSvg => ({
	alt,
	viewBox: svg.match(viewBoxPattern)?.[1] ?? '',
	content: svg.match(svgContentPattern)?.[1]?.trim() ?? '',
	svg,
});

export const getLogoSvgOrDefault = (svg?: string | null): BrandLogoSvg =>
	svg?.trim() ? createBrandLogoSvg('Merchant logo', svg) : QGEN_LOGO_SVG;

/** Central QGen Online wordmark used by platform UI and PDF renderers. */
export const QGEN_LOGO_SVG = createBrandLogoSvg('QGen Online', qgenLogoSvg);

/** Central QGen mark used for compact UI layouts and PDF headers. */
export const QGEN_LOGO_MARK_SVG = createBrandLogoSvg('QGen', qgenLogoMarkSvg);

/** Central Arritech attribution logo used underneath QGen branding. */
export const ARRITECH_LOGO_SVG = createBrandLogoSvg(
	'by Arritech',
	arritechLogoSvg
);

export interface DefaultBrandLogos {
	qgen: BrandLogoSvg;
	qgenMark: BrandLogoSvg;
	arritech: BrandLogoSvg;
}

export interface PdfBrandLogoPayload {
	logoSvg: string;
	logoMarkSvg: string;
	arritechSvg: string;
}

/**
 * Returns the default central brand logo set consumed by UI components and PDFs.
 */
export const getDefaultBrandLogos = (): DefaultBrandLogos => ({
	qgen: QGEN_LOGO_SVG,
	qgenMark: QGEN_LOGO_MARK_SVG,
	arritech: ARRITECH_LOGO_SVG,
});

/**
 * Returns standalone SVG documents for generated PDF templates and API payloads.
 */
export const getPdfBrandLogoPayload = (
	merchantLogoSvg?: string | null
): PdfBrandLogoPayload => ({
	logoSvg: getLogoSvgOrDefault(merchantLogoSvg).svg,
	logoMarkSvg: QGEN_LOGO_MARK_SVG.svg,
	arritechSvg: ARRITECH_LOGO_SVG.svg,
});

/**
 * Logo layout requested from {@link getLogo}:
 * - `default`: the platform/merchant logo with the Arritech attribution below it.
 * - `basic`: the platform/merchant logo only (attribution is placed manually,
 *   e.g. under the sidebar logo).
 */
export type LogoLayout = 'default' | 'basic';

export interface LogoParts {
	/** Platform logo, or the current merchant logo when one is set. */
	mark: BrandLogoSvg;
	/** Arritech attribution shown below the logo, or `null` for the basic layout. */
	attribution: BrandLogoSvg | null;
}

/**
 * Current merchant logo shared across the app. Set once branding is loaded and
 * kept in sync so any consumer of {@link getLogo} reflects it without prop
 * drilling. SVGs are stored as received; sanitize before rendering untrusted
 * markup (as the shared Logo component does).
 */
let currentMerchantLogo: string | null = null;
const logoListeners = new Set<() => void>();

/**
 * Sets (or clears) the current merchant logo and notifies subscribers so shared
 * logo consumers re-render. Pass an empty value to fall back to the QGen logo.
 */
export const setMerchantLogo = (svg?: string | null): void => {
	const next = svg?.trim() ? svg : null;
	if (next === currentMerchantLogo) {
		return;
	}
	currentMerchantLogo = next;
	logoListeners.forEach((listener) => listener());
};

/** Returns the current merchant logo, or `null` when the QGen logo applies. */
export const getMerchantLogo = (): string | null => currentMerchantLogo;

/**
 * Subscribes to merchant logo changes. Returns an unsubscribe function; call it
 * on teardown to avoid leaks.
 */
export const subscribeLogo = (listener: () => void): (() => void) => {
	logoListeners.add(listener);
	return () => {
		logoListeners.delete(listener);
	};
};

/**
 * Resolves the logo pieces for a layout: the merchant logo when set, otherwise
 * the QGen logo, plus the Arritech attribution unless the basic layout is used.
 */
export const getLogoParts = (layout: LogoLayout = 'default'): LogoParts => ({
	mark: getLogoSvgOrDefault(currentMerchantLogo),
	attribution: layout === 'basic' ? null : ARRITECH_LOGO_SVG,
});

const renderLogoSvg = (logo: BrandLogoSvg): string =>
	`<svg xmlns="http://www.w3.org/2000/svg" viewBox="${logo.viewBox}" role="img" aria-label="${logo.alt}">${logo.content}</svg>`;

/**
 * Returns ready-to-render logo markup for any consumer (Vue via `v-html`,
 * templates, PDFs). Defaults to the QGen (or merchant) logo with "by Arritech"
 * below it; pass `basic` for the logo on its own.
 *
 * The markup uses the `brand-logo` class contract styled by the shared Logo
 * component. Merchant SVGs are emitted as stored; sanitize untrusted markup
 * before injecting into a live DOM.
 */
export const getLogo = (layout: LogoLayout = 'default'): string => {
	const { mark, attribution } = getLogoParts(layout);
	const markClass = currentMerchantLogo?.trim()
		? 'brand-logo__mark brand-logo__mark--custom'
		: 'brand-logo__mark';
	const parts = [`<span class="${markClass}">${renderLogoSvg(mark)}</span>`];
	if (attribution) {
		parts.push(
			`<span class="brand-logo__attribution">${renderLogoSvg(attribution)}</span>`
		);
	}
	const rootClass =
		layout === 'basic' ? 'brand-logo brand-logo--basic' : 'brand-logo';
	return `<span class="${rootClass}">${parts.join('')}</span>`;
};
